import { FinancingApi, SubmittingUser } from '@servicetitan/financing-api-client';
import { SubmittingUserStore } from '../features/application/SubmittingUserStore';
import { SetupDataStore, useProviderConfig } from '@servicetitan/financing-workflow-shared';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

export const useSubmittingUser = (enabled = true) => {
    const [apiClient] = useDependencies(FinancingApi);
    const { setupData } = SetupDataStore.useStore();
    const { requiresSubmittingUser } = useProviderConfig();
    const { setSubmittingUser } = SubmittingUserStore.useStore();

    const isBuyFin = setupData?.provider === 'BuyFin';

    const accountResult = useQuery({
        queryKey: [
            'accounts',
            setupData?.tenantId,
            isBuyFin ? 'General' : setupData?.provider,
            isBuyFin ? { generalProvider: setupData?.provider } : null,
        ],
        queryFn: () =>
            apiClient.accountsGet({
                providerName: isBuyFin ? 'General' : (setupData?.provider ?? ''),
                tenantId: setupData?.tenantId ?? null,
                generalProviderName: isBuyFin ? setupData?.provider : undefined,
            }),
        enabled: enabled && !!setupData?.tenantId && requiresSubmittingUser,
    });

    const account = accountResult.data?.data;
    const contactEmail = account?.contactEmails?.[0];

    // Update the store only when the account data changes
    useEffect(() => {
        if (contactEmail) {
            setSubmittingUser({
                firstName: contactEmail,
                lastName: contactEmail,
                email: contactEmail,
            });
        }
    }, [contactEmail, setSubmittingUser]);

    if (accountResult.isLoading) {
        return {
            submittingUser: null,
        };
    }

    if (!account?.contactEmails?.length) {
        if (enabled && requiresSubmittingUser) {
            throw new Error('Account not found or no contact emails available');
        }
        return {
            submittingUser: undefined,
        };
    }

    const submittingUser: SubmittingUser = {
        firstName: contactEmail,
        lastName: contactEmail,
        email: contactEmail,
    };

    return {
        submittingUser,
    };
};
